import { useState } from "react";
import { toast } from "sonner";
import { Pencil } from "lucide-react";
import { useSite, ReloginNeeded } from "@/context/SiteContext";
import { errorMessage } from "@/lib/api";

const EMPTY = "This can't be empty.";

export function EditableText({ k, def, as: Tag = "p", className = "", multiline = false }) {
  const { content, isAdmin, saveContent } = useSite();
  const value = content?.[k] ?? def;
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const tid = k.replace(/[._]/g, "-");

  const text = <Tag className={`${className} whitespace-pre-line`} data-testid={`text-${tid}`}>{value}</Tag>;
  if (!isAdmin) return text;

  const save = async () => {
    if (!draft.trim()) return setError(EMPTY);
    setBusy(true);
    setError("");
    try {
      await saveContent(k, draft);
      setDraft(null);
      toast.success("Saved. Everyone now sees the new text.");
    } catch (err) {
      if (!(err instanceof ReloginNeeded)) setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (draft === null) {
    return (
      <div className="relative rounded-xl outline-2 outline-offset-4 outline-dashed outline-[#24453D]/50">
        {text}
        <button type="button" onClick={() => { setDraft(value); setError(""); }}
          className="mt-2 inline-flex min-h-[44px] items-center gap-1.5 rounded-full border-2 border-dashed border-[#24453D] bg-white px-4 font-sans text-sm font-semibold not-italic tracking-normal text-[var(--pg-ink)] hover:bg-[var(--pg-soft)]"
          data-testid={`edit-${tid}-button`}>
          <Pencil className="h-4 w-4" aria-hidden /> Edit text
        </button>
      </div>
    );
  }

  const Input = multiline ? "textarea" : "input";
  return (
    <div className="rounded-2xl border-2 border-dashed border-[#24453D]/60 bg-white p-4 font-sans text-base font-normal not-italic tracking-normal text-[#1F2328]" data-testid={`edit-${tid}-panel`}>
      <label htmlFor={`edit-${tid}`} className="of-label">Edit text</label>
      <Input id={`edit-${tid}`} className={`of-input ${multiline ? "min-h-[140px] py-3" : ""}`} value={draft} rows={multiline ? 6 : undefined}
        onChange={(e) => { setDraft(e.target.value); setError(""); }} aria-invalid={!!error} data-testid={`edit-${tid}-input`} />
      {error && <p className="of-error" role="alert" data-testid={`edit-${tid}-error`}>{error}</p>}
      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" className="of-btn-soft" onClick={save} disabled={busy} data-testid={`edit-${tid}-save`}>{busy ? "Saving…" : "Save"}</button>
        <button type="button" className="of-btn-ghost" onClick={() => setDraft(null)} data-testid={`edit-${tid}-cancel`}>Cancel</button>
      </div>
    </div>
  );
}
